import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, CheckCircle2, Loader2 } from 'lucide-react';
import { partnerIntakeService } from '../services/partnerIntakeService';
import { scrollToTop } from '../utils/scrollUtils';

const SupportWorkerRegistrationPage: React.FC = () => {
  useEffect(() => {
    document.title = 'Become a Support Worker - AbleGo';
    scrollToTop('instant');
  }, []);

  const [full_name, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [town_or_region, setTownOrRegion] = useState('');
  const [date_of_birth, setDateOfBirth] = useState('');
  const [experience_summary, setExperienceSummary] = useState('');
  const [has_current_dbs, setHasCurrentDbs] = useState(false);
  const [availability_note, setAvailabilityNote] = useState('');
  const [agrees_video_validation, setAgreesVideoValidation] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reference, setReference] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await partnerIntakeService.submitSupportWorker({
        full_name,
        email,
        phone,
        town_or_region,
        date_of_birth,
        experience_summary,
        has_current_dbs,
        availability_note: availability_note.trim() || undefined,
        agrees_video_validation,
      });
      if (!result.success) {
        setError(result.error || 'Something went wrong. Please try again.');
        return;
      }
      setReference(result.reference || null);
    } finally {
      setSubmitting(false);
    }
  };

  if (reference) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50/35 to-green-50/25 dark:from-dark-900 dark:to-dark-800 pt-20 pb-16">
        <div className="container mx-auto px-4 max-w-lg">
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-lg border border-gray-100 dark:border-dark-700 p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-50 mb-3">
              Application received
            </h1>
            <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed mb-2">
              Thank you. We will email you shortly. Your reference is{' '}
              <strong className="text-gray-900 dark:text-gray-100">{reference}</strong>.
            </p>
            <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed mb-6">
              <strong>Next step:</strong> we will arrange a short video call to validate your submission
              and walk you through safeguarding and onboarding. There is no platform account yet —
              we create that after verification.
            </p>
            <Link
              to="/"
              className="inline-block px-6 py-3 rounded-xl bg-teal-600 text-white font-medium hover:bg-teal-700 transition-colors"
            >
              Back to home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50/35 to-green-50/25 dark:from-dark-900 dark:to-dark-800 pt-20 pb-16">
      <div className="container mx-auto px-4 max-w-2xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-teal-600 text-white shadow-lg mb-4">
            <Heart className="w-7 h-7" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-50 tracking-tight">
            Support riders with AbleGo
          </h1>
          <p className="mt-3 text-gray-600 dark:text-gray-300 text-sm max-w-md mx-auto leading-relaxed">
            Tell us briefly who you are and your experience — one page only. Documents and contracting
            come after we have spoken with you on a video call.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="bg-white dark:bg-dark-800 rounded-2xl shadow-lg border border-gray-100 dark:border-dark-700 p-6 sm:p-8 space-y-8"
        >
          <section>
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4">
              Your details
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="sm:col-span-2 block">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Full name</span>
                <input
                  required
                  value={full_name}
                  onChange={(e) => setFullName(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-gray-200 dark:border-dark-600 dark:bg-dark-900 px-3 py-2.5 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Email</span>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-gray-200 dark:border-dark-600 dark:bg-dark-900 px-3 py-2.5 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Phone</span>
                <input
                  type="tel"
                  required
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-gray-200 dark:border-dark-600 dark:bg-dark-900 px-3 py-2.5 text-sm"
                />
              </label>
              <label className="sm:col-span-2 block">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  Town or region
                </span>
                <input
                  required
                  placeholder="e.g. Somerset, Cardiff"
                  value={town_or_region}
                  onChange={(e) => setTownOrRegion(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-gray-200 dark:border-dark-600 dark:bg-dark-900 px-3 py-2.5 text-sm"
                />
              </label>
              <label className="block sm:col-span-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  Date of birth
                </span>
                <input
                  type="date"
                  required
                  value={date_of_birth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-gray-200 dark:border-dark-600 dark:bg-dark-900 px-3 py-2.5 text-sm max-w-[200px]"
                />
              </label>
            </div>
          </section>

          <section>
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4">
              Experience & availability
            </h2>
            <label className="block mb-4">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                Brief summary of your care / support experience
              </span>
              <textarea
                required
                minLength={20}
                rows={5}
                value={experience_summary}
                onChange={(e) => setExperienceSummary(e.target.value)}
                placeholder="For example: two years supporting adults with learning disabilities, moving and handling trained, calm under pressure..."
                className="mt-1 w-full rounded-xl border border-gray-200 dark:border-dark-600 dark:bg-dark-900 px-3 py-2.5 text-sm resize-y min-h-[120px]"
              />
              <span className="text-xs text-gray-500 mt-1 block">
                A few sentences is enough at this stage (minimum 20 characters).
              </span>
            </label>

            <label className="flex items-center gap-3 mb-4 cursor-pointer">
              <input
                type="checkbox"
                checked={has_current_dbs}
                onChange={(e) => setHasCurrentDbs(e.target.checked)}
                className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                I hold a current DBS check suitable for this role (or equivalent where applicable).
              </span>
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                Typical availability <span className="font-normal text-gray-500">(optional)</span>
              </span>
              <input
                placeholder="e.g. weekday afternoons, occasional weekends"
                value={availability_note}
                onChange={(e) => setAvailabilityNote(e.target.value)}
                maxLength={500}
                className="mt-1 w-full rounded-xl border border-gray-200 dark:border-dark-600 dark:bg-dark-900 px-3 py-2.5 text-sm"
              />
            </label>
          </section>

          <div className="rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-100 dark:border-amber-900/40 px-4 py-3">
            <p className="text-sm text-amber-950 dark:text-amber-100 leading-relaxed">
              <strong>Video validation:</strong> we use a short video call to confirm identity and
              suitability before inviting you onto the platform. Submitting this form does not create
              an account.
            </p>
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={agrees_video_validation}
              onChange={(e) => setAgreesVideoValidation(e.target.checked)}
              className="mt-1 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300 leading-snug">
              I understand AbleGo will contact me to arrange a video call before my application is
              finalised.
            </span>
          </label>

          {error && (
            <div className="rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-100 px-4 py-3 text-sm text-red-800 dark:text-red-200">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || !agrees_video_validation}
            className="w-full py-3.5 rounded-xl bg-teal-600 text-white font-semibold hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Sending…
              </>
            ) : (
              'Submit application'
            )}
          </button>

          <p className="text-center text-xs text-gray-500">
            Already registered?{' '}
            <Link to="/login" className="text-teal-600 hover:underline font-medium">
              Sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default SupportWorkerRegistrationPage;
